import {
  createTemplateTheme,
  type CodeComponentTemplateBranding,
} from './branding';

export function createShaderSource(
  branding: CodeComponentTemplateBranding = {}
): string {
  const {primaryColor} = createTemplateTheme(branding, '#2563eb');
  const surfaceColor = JSON.stringify(branding.surfaceColor ?? '#ffffff');

  return `import { declareComponent } from "@webflow/react";
import { props } from "@webflow/data-types";
import { useEffect, useRef, useState } from "react";

/* ── shader source: paper/ink dithered waves ── */
const VERT_SRC = [
  "attribute vec2 aPos;",
  "void main() {",
  "  gl_Position = vec4(aPos, 0.0, 1.0);",
  "}",
].join("\\n");

const FRAG_SRC = [
  "precision highp float;",
  "uniform vec2 uResolution;",
  "uniform float uTime;",
  "uniform vec3 uPaper;",
  "uniform vec3 uInk;",
  "",
  "float hash(vec2 p) {",
  "  p = fract(p * vec2(123.34, 456.21));",
  "  p += dot(p, p + 45.32);",
  "  return fract(p.x * p.y);",
  "}",
  "",
  "float noise(vec2 p) {",
  "  vec2 i = floor(p);",
  "  vec2 f = fract(p);",
  "  float a = hash(i);",
  "  float b = hash(i + vec2(1.0, 0.0));",
  "  float c = hash(i + vec2(0.0, 1.0));",
  "  float d = hash(i + vec2(1.0, 1.0));",
  "  vec2 u = f * f * (3.0 - 2.0 * f);",
  "  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;",
  "}",
  "",
  "float fbm(vec2 p) {",
  "  float value = 0.0;",
  "  float amp = 0.5;",
  "  for (int i = 0; i < 5; i++) {",
  "    value += amp * noise(p);",
  "    p *= 2.02;",
  "    amp *= 0.5;",
  "  }",
  "  return value;",
  "}",
  "",
  "float warpedFbm(vec2 p) {",
  "  vec2 q = vec2(fbm(p + vec2(0.0, 0.0)), fbm(p + vec2(5.2, 1.3)));",
  "  vec2 r = vec2(fbm(p + 4.0 * q + vec2(1.7, 9.2)), fbm(p + 4.0 * q + vec2(8.3, 2.8)));",
  "  return fbm(p + 4.0 * r);",
  "}",
  "",
  "void main() {",
  "  vec2 uv = gl_FragCoord.xy / uResolution.xy;",
  "  float aspect = uResolution.x / uResolution.y;",
  "  vec2 p = uv;",
  "  p.x *= aspect;",
  "",
  "  vec2 dir = normalize(vec2(1.0, 0.35));",
  "  float freq = 6.0;",
  "  float speed = 0.55;",
  "  float phase = dot(p, dir) * freq - uTime * speed;",
  "  float swell = sin(phase) + sin(phase * 2.07 + 1.3) * 0.5;",
  "",
  "  vec2 dir2 = normalize(vec2(-0.4, 1.0));",
  "  float freq2 = 3.2;",
  "  float speed2 = 0.22;",
  "  float phase2 = dot(p, dir2) * freq2 - uTime * speed2;",
  "  float crossSwell = sin(phase2) * 0.6;",
  "",
  "  vec2 warpCoord = p * 2.4 + vec2(uTime * 0.06, -uTime * 0.04);",
  "  float chop = warpedFbm(warpCoord) - 0.5;",
  "",
  "  float combined = swell * 0.35 + crossSwell * 0.2 + chop * 0.35;",
  "  float lum = smoothstep(-1.3, 1.3, combined + 0.35);",
  "",
  "  float ditherScale = 2.0;",
  "  vec2 dCoord = floor(gl_FragCoord.xy / ditherScale);",
  "  float b2 = fract(dCoord.x * 0.5 + dCoord.y * dCoord.y * 0.75);",
  "  vec2 half2 = floor(dCoord * 0.5);",
  "  float b2h = fract(half2.x * 0.5 + half2.y * half2.y * 0.75);",
  "  vec2 quarter2 = floor(dCoord * 0.25);",
  "  float b2q = fract(quarter2.x * 0.5 + quarter2.y * quarter2.y * 0.75);",
  "  float bayer8 = fract((b2q * 0.25 + b2h) * 0.25 + b2);",
  "",
  "  float mask = step(bayer8, lum);",
  "  vec3 color = mix(uInk, uPaper, mask);",
  "",
  "  gl_FragColor = vec4(color, 1.0);",
  "}",
].join("\\n");

function hexToRgb01(hex, fallback) {
  const raw = (hex || "").replace("#", "").trim();
  if (!/^[0-9a-f]{6}$/i.test(raw)) return fallback;
  const n = parseInt(raw, 16);
  return [(n >> 16) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

const SURFACE = ${surfaceColor};

// Ink is the brand color; paper is a soft tint of it over the site surface.
function waveColors(color) {
  const ink = hexToRgb01(color, [0.145, 0.388, 0.922]);
  const surface = hexToRgb01(SURFACE, [1, 1, 1]);
  return { ink, paper: ink.map((c, i) => surface[i] + (c - surface[i]) * 0.16) };
}

function tint(color) {
  return "color-mix(in srgb, " + color + " 16%, " + SURFACE + ")";
}

function compileShader(gl, type, src) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error("Shader compile error:", gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

/* ── main component ── */
export function Shader({ primaryColor = ${primaryColor}, height = 480 }) {
  const canvasRef = useRef(null);
  const colorsRef = useRef(waveColors(primaryColor));
  const drawRef = useRef(null);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    colorsRef.current = waveColors(primaryColor);
    drawRef.current?.();
  }, [primaryColor]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas?.getContext("webgl") || canvas?.getContext("experimental-webgl");
    if (!gl) {
      setShowFallback(true);
      return;
    }

    const vertShader = compileShader(gl, gl.VERTEX_SHADER, VERT_SRC);
    const fragShader = compileShader(gl, gl.FRAGMENT_SHADER, FRAG_SRC);
    if (!vertShader || !fragShader) {
      setShowFallback(true);
      return;
    }

    const program = gl.createProgram();
    gl.attachShader(program, vertShader);
    gl.attachShader(program, fragShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error("Program link error:", gl.getProgramInfoLog(program));
      setShowFallback(true);
      return;
    }
    gl.useProgram(program);

    const vertices = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
    const vbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
    const aPosLoc = gl.getAttribLocation(program, "aPos");
    gl.enableVertexAttribArray(aPosLoc);
    gl.vertexAttribPointer(aPosLoc, 2, gl.FLOAT, false, 0, 0);

    const uResolutionLoc = gl.getUniformLocation(program, "uResolution");
    const uTimeLoc = gl.getUniformLocation(program, "uTime");
    const uPaperLoc = gl.getUniformLocation(program, "uPaper");
    const uInkLoc = gl.getUniformLocation(program, "uInk");

    const startTime = performance.now();
    const draw = () => {
      const { paper, ink } = colorsRef.current;
      gl.uniform2f(uResolutionLoc, canvas.width, canvas.height);
      gl.uniform1f(uTimeLoc, (performance.now() - startTime) / 1000);
      gl.uniform3f(uPaperLoc, paper[0], paper[1], paper[2]);
      gl.uniform3f(uInkLoc, ink[0], ink[1], ink[2]);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };
    drawRef.current = draw;

    // Size the drawing buffer to the component, not the window.
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.floor(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.floor(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    // Animate only while visible; reduced motion keeps a single still frame.
    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let inView = true;
    let rafId = 0;
    const loop = () => {
      draw();
      rafId = requestAnimationFrame(loop);
    };
    const sync = () => {
      const shouldAnimate = !prefersReduced && inView && !document.hidden;
      if (shouldAnimate && !rafId) rafId = requestAnimationFrame(loop);
      if (!shouldAnimate && rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      resize();
      draw();
    });
    resizeObserver.observe(canvas);
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      sync();
    });
    intersectionObserver.observe(canvas);
    document.addEventListener("visibilitychange", sync);
    resize();
    draw();
    sync();

    return () => {
      cancelAnimationFrame(rafId);
      drawRef.current = null;
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener("visibilitychange", sync);
      gl.deleteBuffer(vbo);
      gl.deleteProgram(program);
      gl.deleteShader(vertShader);
      gl.deleteShader(fragShader);
    };
  }, []);

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ height, backgroundColor: tint(primaryColor) }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 block h-full w-full"
        style={{ display: showFallback ? "none" : "block" }}
        aria-hidden="true"
      />
      {showFallback && (
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, " + tint(primaryColor) + " 0%, " + primaryColor + " 100%)",
          }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default declareComponent(Shader, {
  name: "Shader",
  description: "Animated dithered wave background in your brand color, rendered with WebGL.",
  group: "Sections",
  props: {
    primaryColor: props.Text({
      name: "Primary Brand Color",
      defaultValue: ${primaryColor},
    }),
    height: props.Number({
      name: "Height",
      defaultValue: 480,
      min: 120,
      max: 1600,
    }),
  },
});
`;
}

export const SHADER_SOURCE = createShaderSource();
